import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import CustomTable from "components/Table/CustomTable";
import { FC, useState } from "react";
import { Modal } from "components/Modal/Modal";
import { LearnersDetail } from "./LearnersDetail";
import { ProcessedLearner } from "../learnersAnalytics";
import { UserWithPet } from "components/VetStore/CoachPetAvatar";

type Props = {
  learners: ProcessedLearner[];
};

export const LearnersFeedbackTable: FC<Props> = ({ learners }) => {
  const [selectedLearner, setSelectedLearner] =
    useState<ProcessedLearner | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (learner: ProcessedLearner) => {
    setSelectedLearner(learner);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLearner(null);
  };

  return (
    <Box>
      <CustomTable
        entities={learners}
        handleActionClick={() => {}}
        fieldToFilter={(row, filterText) =>
          (row.student.name || row.student.email || "")
            .toLowerCase()
            .includes(filterText.toLowerCase())
        }
        columns={[
          {
            id: "name",
            label: "Name",
            value: (row: ProcessedLearner) => (
              <Stack direction="row" spacing={2} alignItems="center">
                <UserWithPet
                  pictureUrl={row.student.picture_url}
                  name={row.student.name}
                  equippedStoreItems={row.student.equipped_store_items}
                  avatarSize={40}
                />
                <Box>
                  <Typography variant="body2">{row.student.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.student.email}
                  </Typography>
                </Box>
              </Stack>
            ),
          },
          {
            id: "requirements",
            label: "Requirements",
            value: (row: ProcessedLearner) => row.requirements.length,
          },
          {
            id: "onTrack",
            label: "On track",
            value: (row: ProcessedLearner) => (
              <Chip
                label={row.onTrackCount}
                color="success"
                size="small"
                variant="outlined"
              />
            ),
          },
          {
            id: "behind",
            label: "Behind",
            value: (row: ProcessedLearner) => (
              <Chip
                label={row.behindCount}
                color={row.behindCount > 0 ? "warning" : "default"}
                size="small"
                variant="outlined"
              />
            ),
          },
          {
            id: "feedbacks",
            label: "Feedbacks",
            value: (row: ProcessedLearner) => row.totalFeedbacks,
          },
          {
            id: "details",
            value: (row: ProcessedLearner) => (
              <Button
                color="primary"
                variant="outlined"
                size="small"
                sx={{ cursor: "pointer" }}
                onClick={() => handleOpenModal(row)}
              >
                Details
              </Button>
            ),
          },
        ]}
        isSelectable={false}
        idColumn="studentId"
        extraActionItems={[]}
        enableCardViewInSmallDevices
        enableMenuInSmallDevices={false}
      />

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Learner details"
        size="large"
      >
        {selectedLearner && <LearnersDetail learner={selectedLearner} />}
      </Modal>
    </Box>
  );
};
